export const TASK_TYPES = {
  BASE: [1, 2, 3, 4, 5, 6, 7, 10, 100] as const,
  DRIVER_CODES: [8, 9] as const,
  RECONFIGURATION: [11] as const,
  GS_SPECIFIC: Array(1036 - 1001) // [1001, ... , 1035]
    .fill(0)
    .map((_, i) => 1001 + i),
} as const

export const TASK_STATUSES = {
  CREATED: 'Created',
  STARTED: 'Started',
  FAILED: 'Failed',
  SUCCESSED: 'Successed',
  SUSPENDED: 'Suspended',
  DELETED: 'Deleted',
} as const

export type TaskStatus = (typeof TASK_STATUSES)[keyof typeof TASK_STATUSES]

export enum TaskEntityType {
  Agents = 1,
  Users = 2,
  Models = 3,
  Vehicles = 4,
  Drivers = 5,
  DriverVehicleAssignments = 6,
  Geozones = 7,
  Retranslations = 10,
  Messages = 100,
  DeviceRedirector = 11,
}

export const TaskEntityNames: Record<TaskEntityType, string> = {
  [TaskEntityType.Agents]: 'Агенты',
  [TaskEntityType.Users]: 'Пользователи',
  [TaskEntityType.Models]: 'Модели',
  [TaskEntityType.Vehicles]: 'Транспорт',
  [TaskEntityType.Drivers]: 'Водители',
  [TaskEntityType.DriverVehicleAssignments]: 'Назначения',
  [TaskEntityType.Geozones]: 'Геозоны',
  [TaskEntityType.Retranslations]: 'Ретрансляторы',
  [TaskEntityType.Messages]: 'Сообщения',
  [TaskEntityType.DeviceRedirector]: 'Перенастроено',
}

export type TaskEntityKey = keyof typeof TaskEntityType

export interface TaskEntityStats {
  total: number
  processed: number
  percent: number
  displayText: string
}

export interface AllTaskStats {
  Agents: TaskEntityStats
  Users: TaskEntityStats
  Models: TaskEntityStats
  Vehicles: TaskEntityStats
  Drivers: TaskEntityStats
  DriverVehicleAssignments: TaskEntityStats
  Geozones: TaskEntityStats
  Retranslations: TaskEntityStats
  Messages: TaskEntityStats
  DeviceRedirector: TaskEntityStats
}

export const entityTypeToStatsKeyMap: Record<number, keyof AllTaskStats> = {
  1: 'Agents',
  2: 'Users',
  3: 'Models',
  4: 'Vehicles',
  5: 'Drivers',
  6: 'DriverVehicleAssignments',
  7: 'Geozones',
  10: 'Retranslations',
  100: 'Messages',
  11: 'DeviceRedirector',
}

export const getDefaultStats = (): AllTaskStats => ({
  Agents: { total: 0, processed: 0, percent: 0, displayText: '0 из 0' },
  Users: { total: 0, processed: 0, percent: 0, displayText: '0 из 0' },
  Models: { total: 0, processed: 0, percent: 0, displayText: '0 из 0' },
  Vehicles: { total: 0, processed: 0, percent: 0, displayText: '0 из 0' },
  Drivers: { total: 0, processed: 0, percent: 0, displayText: '0 из 0' },
  DriverVehicleAssignments: {
    total: 0,
    processed: 0,
    percent: 0,
    displayText: '0 из 0',
  },
  Geozones: { total: 0, processed: 0, percent: 0, displayText: '0 из 0' },
  Retranslations: { total: 0, processed: 0, percent: 0, displayText: '0 из 0' },
  Messages: { total: 0, processed: 0, percent: 0, displayText: '0 из 0' },
  DeviceRedirector: {
    total: 0,
    processed: 0,
    percent: 0,
    displayText: '0 из 0',
  },
})

export const formatEntityStats = (
  stats: AllTaskStats,
  formatLargeNumber: (num: number) => string
): AllTaskStats => {
  return {
    Agents: {
      ...stats.Agents,
      displayText: `${formatLargeNumber(stats.Agents.processed)} из ${formatLargeNumber(stats.Agents.total)}`,
    },
    Users: {
      ...stats.Users,
      displayText: `${formatLargeNumber(stats.Users.processed)} из ${formatLargeNumber(stats.Users.total)}`,
    },
    Models: {
      ...stats.Models,
      displayText: `${formatLargeNumber(stats.Models.processed)} из ${formatLargeNumber(stats.Models.total)}`,
    },
    Vehicles: {
      ...stats.Vehicles,
      displayText: `${formatLargeNumber(stats.Vehicles.processed)} из ${formatLargeNumber(stats.Vehicles.total)}`,
    },
    Drivers: {
      ...stats.Drivers,
      displayText: `${formatLargeNumber(stats.Drivers.processed)} из ${formatLargeNumber(stats.Drivers.total)}`,
    },
    DriverVehicleAssignments: {
      ...stats.DriverVehicleAssignments,
      displayText: `${formatLargeNumber(stats.DriverVehicleAssignments.processed)} из ${formatLargeNumber(stats.DriverVehicleAssignments.total)}`,
    },
    Geozones: {
      ...stats.Geozones,
      displayText: `${formatLargeNumber(stats.Geozones.processed)} из ${formatLargeNumber(stats.Geozones.total)}`,
    },
    Retranslations: {
      ...stats.Retranslations,
      displayText: `${formatLargeNumber(stats.Retranslations.processed)} из ${formatLargeNumber(stats.Retranslations.total)}`,
    },
    Messages: {
      ...stats.Messages,
      displayText: `${formatLargeNumber(stats.Messages.processed)} из ${formatLargeNumber(stats.Messages.total)}`,
    },
    DeviceRedirector: {
      ...stats.DeviceRedirector,
      displayText: `${formatLargeNumber(stats.DeviceRedirector.processed)} из ${formatLargeNumber(stats.DeviceRedirector.total)}`,
    },
  }
}

export interface TaskLog {
  time: string
  type: 'info' | 'warning' | 'error' | 'access'
  message: string
}

export interface TaskLogsSummary {
  info: number
  warning: number
  error: number
}

export interface TaskItem {
  id: string
  status:
    | 'Created'
    | 'Started'
    | 'Failed'
    | 'Successed'
    | 'Suspended'
    | 'Deleted'
  createdAt: string
  startedAt: string
  completedAt: string
  progress: number
  period: number
  number: number
  uploadAccount: string
  downloadLogin: string
  downloadAccount: string
  stats: AllTaskStats
  hasErrors: boolean
  errorMessage: string
  system: string
  vehicleIds?: string[]
  isReconfiguration?: boolean
}

export const getDefaultTaskItem = (): TaskItem => ({
  id: '',
  status: TASK_STATUSES.CREATED,
  createdAt: '',
  startedAt: '',
  completedAt: '',
  progress: 0,
  period: 0,
  number: 0,
  uploadAccount: '',
  downloadAccount: '',
  downloadLogin: '',
  stats: getDefaultStats(),
  hasErrors: false,
  errorMessage: '',
  system: '',
})
